import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Snackbar, Stack, CircularProgress,
  List, ListItem, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';

export default function WarehousesPage() {
  const { isReadOnly } = useReadOnly();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Create warehouse dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', address: '', timezone: 'UTC' });
  const [saving, setSaving] = useState(false);

  // Locations dialog
  const [locWarehouse, setLocWarehouse] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [newLocLabel, setNewLocLabel] = useState('');

  // Transfer dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ fromWarehouseId: '', toWarehouseId: '', notes: '', items: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tenantApi.listWarehouses();
      setWarehouses(data.warehouses || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await tenantApi.createWarehouse(form);
      setToast({ open: true, message: `Warehouse "${form.name}" created.`, severity: 'success' });
      setCreateOpen(false);
      setForm({ name: '', code: '', address: '', timezone: 'UTC' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    finally { setSaving(false); }
  };

  const openLocations = async (wh) => {
    setLocWarehouse(wh);
    setLocLoading(true);
    try {
      const data = await tenantApi.listLocations(wh.warehouseId);
      setLocations(data.locations || []);
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    finally { setLocLoading(false); }
  };

  const handleAddLocation = async () => {
    if (!newLocLabel) { return; }
    try {
      await tenantApi.createLocation(locWarehouse.warehouseId, { label: newLocLabel });
      setNewLocLabel('');
      openLocations(locWarehouse);
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  const handleTransfer = async () => {
    try {
      await tenantApi.createTransferOrder(transferForm);
      setToast({ open: true, message: 'Transfer order created.', severity: 'success' });
      setTransferOpen(false);
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Warehouses</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => setTransferOpen(true)}
            disabled={isReadOnly || warehouses.length < 2}>
            Transfer Order
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={isReadOnly}>
            Add Warehouse
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Timezone</TableCell>
              <TableCell>Default</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : warehouses.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No warehouses yet.</TableCell></TableRow>
            ) : warehouses.map((wh) => (
              <TableRow key={wh.warehouseId} hover>
                <TableCell fontWeight={600}>{wh.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{wh.code}</TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{wh.address || '—'}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{wh.timezone}</TableCell>
                <TableCell>
                  {wh.isDefault && <Chip label="Default" size="small" color="primary" />}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Bin Locations">
                    <IconButton size="small" onClick={() => openLocations(wh)}>
                      <LocationOnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Create warehouse dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Warehouse</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {[['name', 'Name *'], ['code', 'Code *'], ['address', 'Address'], ['timezone', 'Timezone']].map(([field, label]) => (
              <TextField key={field} label={label} size="small" fullWidth
                value={form[field]}
                onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.name || !form.code}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bin locations dialog */}
      <Dialog open={!!locWarehouse} onClose={() => setLocWarehouse(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Bin Locations — {locWarehouse?.name}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} mb={2}>
            <TextField label="New Location Label" size="small" fullWidth value={newLocLabel}
              onChange={(e) => setNewLocLabel(e.target.value)}
              placeholder="e.g. A1-B2-L3" />
            <Button variant="outlined" onClick={handleAddLocation} disabled={!newLocLabel}>Add</Button>
          </Stack>
          {locLoading ? <CircularProgress size={20} /> : (
            <List dense>
              {locations.length === 0
                ? <ListItem><ListItemText primary="No locations yet." /></ListItem>
                : locations.map((loc) => (
                  <ListItem key={loc.locationId} divider>
                    <ListItemText primary={loc.label}
                      secondary={[loc.aisle && `Aisle ${loc.aisle}`, loc.bay && `Bay ${loc.bay}`, loc.bin && `Bin ${loc.bin}`].filter(Boolean).join(' · ')} />
                  </ListItem>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocWarehouse(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer order dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Transfer Order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="From Warehouse ID" size="small" fullWidth
              value={transferForm.fromWarehouseId}
              onChange={(e) => setTransferForm((f) => ({ ...f, fromWarehouseId: e.target.value }))} />
            <TextField label="To Warehouse ID" size="small" fullWidth
              value={transferForm.toWarehouseId}
              onChange={(e) => setTransferForm((f) => ({ ...f, toWarehouseId: e.target.value }))} />
            <TextField label="Notes" size="small" fullWidth multiline rows={2}
              value={transferForm.notes}
              onChange={(e) => setTransferForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTransfer}
            disabled={!transferForm.fromWarehouseId || !transferForm.toWarehouseId}>
            Create Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
